import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, ShieldCheck, BadgeCheck, Landmark } from "lucide-react";

const PDF_URL =
  "https://customer-assets-rejwkqb3.emergentagent.net/job_8c3360b9-8f6d-4242-8ae2-a370d05a81f8/artifacts/lwplmz2p_SPICE%20%2B%20Part%20B_Approval%20Letter_AC6266640.pdf";

const MCA_DETAILS = [
  ["Company Name", "NESHA GLOBAL PRIVATE LIMITED"],
  ["CIN", "U46309UT2026PTC022410"],
  ["Date of Incorporation", "28 September 2026"],
  ["Issuing Authority", "Registrar of Companies, Central Registration Centre, Manesar — Ministry of Corporate Affairs, Government of India"],
  ["Registered Office", "C/o Amit Kumar, Chharba, Langha Road, Sahaspur (Dehradun), Vikasnagar, Dehradun — 248197, Uttarakhand"],
  ["Company Type", "Company limited by shares (Private Limited)"],
];

const CertModal = ({ onClose }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <motion.div
      data-testid="certificate-modal"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[90] flex items-center justify-center p-4 sm:p-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-forest/85 backdrop-blur-md" />
      <motion.div
        initial={{ y: 40, scale: 0.96, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: 30, scale: 0.97, opacity: 0 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-cream rounded-2xl shadow-2xl border border-gold/40"
      >
        <div className="sticky top-0 flex items-center justify-between bg-cream/95 backdrop-blur border-b border-forest/10 px-6 sm:px-8 py-5">
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-forest text-gold flex items-center justify-center">
              <Landmark className="w-5 h-5" />
            </span>
            <div>
              <p className="font-grotesk font-semibold text-forest leading-tight">
                Certificate of Incorporation
              </p>
              <p className="font-mono text-[10px] tracking-[0.2em] text-mutedtext uppercase">
                MCA SPICE+ — Govt. of India
              </p>
            </div>
          </div>
          <button
            data-testid="certificate-modal-close"
            onClick={onClose}
            className="w-10 h-10 rounded-full border border-forest/20 flex items-center justify-center text-forest hover:bg-forest hover:text-cream transition-colors duration-300"
            aria-label="Close certificate modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 sm:px-8 py-7 space-y-5">
          {MCA_DETAILS.map(([k, v]) => (
            <div key={k} className="grid sm:grid-cols-3 gap-1 sm:gap-4">
              <p className="font-mono text-[10px] tracking-[0.2em] text-sunset uppercase pt-1">
                {k}
              </p>
              <p className="sm:col-span-2 text-sm leading-relaxed text-charcoal/85">{v}</p>
            </div>
          ))}

          <div className="pt-4 border-t border-forest/10 flex flex-col sm:flex-row gap-3">
            <a
              data-testid="certificate-pdf-link"
              href={PDF_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-forest text-cream px-6 py-3 font-grotesk text-sm font-semibold hover:bg-forest-surface transition-colors duration-300"
            >
              <FileText className="w-4 h-4" />
              View Original Document (PDF)
            </a>
            <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.15em] text-mutedtext uppercase px-2">
              <ShieldCheck className="w-4 h-4 text-forest" />
              Verifiable at mca.gov.in
            </span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const Certificate = () => {
  const [modalOpen, setModalOpen] = useState(false);

  const cards = [
    {
      id: "mca",
      icon: Landmark,
      tag: "Ministry of Corporate Affairs",
      title: "Certificate of Incorporation",
      sub: "SPICE+ · Registrar of Companies",
      lines: [
        ["CIN", "U46309UT2026PTC022410"],
        ["Incorporated", "28 September 2026"],
        ["Status", "Active Statutory Incorporation"],
      ],
      action: true,
    },
    {
      id: "pan",
      icon: BadgeCheck,
      tag: "Income Tax Department",
      title: "Permanent Account Number",
      sub: "e-PAN · Company Account",
      lines: [
        ["PAN", "AAMCN1442L"],
        ["Holder", "NESHA GLOBAL PRIVATE LIMITED"],
        ["Category", "Private Limited Company"],
      ],
    },
    {
      id: "tan",
      icon: ShieldCheck,
      tag: "Income Tax Department",
      title: "Tax Deduction Account Number",
      sub: "TAN Allotment · Income Tax Act, 2025",
      lines: [
        ["TAN", "MRTN11614B"],
        ["Holder", "NESHA GLOBAL PRIVATE LIMITED"],
        ["Use", "TDS, TCS & statutory compliance"],
      ],
    },
  ];

  return (
    <section
      id="certificate"
      data-testid="certificate-section"
      className="relative bg-cream-card py-20 sm:py-28 overflow-hidden"
    >
      <div className="px-6 sm:px-12 lg:px-20 max-w-7xl mx-auto">
        <div className="max-w-2xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-[10px] sm:text-xs tracking-[0.4em] text-sunset uppercase mb-5"
          >
            03 — Certificate
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="font-display text-3xl sm:text-4xl lg:text-5xl text-forest tracking-tight leading-[1.08]"
          >
            Government registered. <span className="italic text-mud-accent">Globally trusted.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 text-sm sm:text-base leading-relaxed text-mutedtext"
          >
            Informed by integrity, verified by law. Our official incorporation and tax
            registrations with the Government of India.
          </motion.p>
        </div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {cards.map((c, i) => (
            <motion.div
              key={c.id}
              data-testid={`certificate-card-${c.id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              whileHover={{ y: -8 }}
              className={`h-full flex flex-col rounded-2xl p-8 border shadow-sm transition-colors duration-300 ${
                c.id === "mca"
                  ? "bg-forest text-cream border-gold/30"
                  : "bg-cream text-charcoal border-forest/15"
              }`}
            >
              <div className="flex items-center justify-between">
                <span
                  className={`w-12 h-12 rounded-full flex items-center justify-center ${
                    c.id === "mca" ? "bg-gold/15 text-gold" : "bg-forest text-gold"
                  }`}
                >
                  <c.icon className="w-5 h-5" />
                </span>
                <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-right opacity-60">
                  {c.tag}
                </span>
              </div>

              <h3
                data-testid={`certificate-title-${c.id}`}
                className="mt-6 font-grotesk font-semibold text-xl leading-snug"
              >
                {c.title}
              </h3>
              <p
                className={`mt-1 font-mono text-[10px] tracking-[0.2em] uppercase ${
                  c.id === "mca" ? "text-gold/80" : "text-sunset"
                }`}
              >
                {c.sub}
              </p>

              <dl className="mt-6 space-y-3 flex-1">
                {c.lines.map(([k, v]) => (
                  <div key={k} className="flex flex-col gap-0.5">
                    <dt className="font-mono text-[9px] tracking-[0.25em] uppercase opacity-50">
                      {k}
                    </dt>
                    <dd className={`font-mono text-sm ${c.id === "mca" ? "text-cream" : "text-charcoal"}`}>
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>

              {c.action ? (
                <button
                  data-testid="certificate-mca-view-button"
                  onClick={() => setModalOpen(true)}
                  className="mt-8 w-full rounded-full bg-sunset py-3.5 font-grotesk text-sm font-semibold uppercase tracking-wider text-forest hover:bg-sunset-bright transition-colors duration-300"
                >
                  View Certificate
                </button>
              ) : (
                <div
                  className={`mt-8 w-full rounded-full py-3.5 text-center font-mono text-[10px] tracking-[0.25em] uppercase border ${
                    c.id === "mca"
                      ? "border-gold/40 text-gold"
                      : "border-forest/20 text-forest/60"
                  }`}
                >
                  Verified Registration
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {modalOpen && <CertModal onClose={() => setModalOpen(false)} />}
      </AnimatePresence>
    </section>
  );
};

export default Certificate;
